// Profile along a drawn line: a bent line across the map, unfolded into one vertical section showing the ground,
// the formations under it (3D geology model) and the passages within a corridor either side, swung onto the line.
// Labels can be added and dragged; profiles are saved for everyone (contributors can label any profile).
import { useState, useEffect, useRef, useMemo } from 'preact/hooks';
import { html, notify, ConfirmButton } from './common.js';
import { get, post, patch, del, api } from '../api.js';
import { viewer, profileUI, places, model, opts, filter, geology, canWrite, selection, cavePlaces, placeLabel, feat } from '../store.js';
import { Polyline, passagesNear, entrancesNear, strataImage, planNear, featuresNear } from '../viewer/profiledata.js';
import { fmtLen } from '../geo.js';

const FT = 0.3048;
const PAPER = '#f7f4ec', INK = '#1f1e1b', INK2 = '#5d5a52', GROUND = '#4a3424', LINE_COL = '#8b5cf6';
const LABEL_COLOURS = ['#1f1e1b', '#c0392b', '#1f6fd1', '#0f8a5f', '#b26b00', '#8b5cf6'];
const VES = [1, 1.5, 2, 3, 5, 10];
const M0 = { l: 58, r: 52, t: 30, b: 26 };      // plot margins (px); the top grows when the legend is shown

const snapshot = (d) => JSON.stringify(d ? { name: d.name, description: d.description || null, line: d.line, corridor_m: d.corridor_m,
  ve: d.ve, labels: d.labels, options: d.options, visibility: d.visibility } : null);
const newDoc = (line) => ({ id: null, name: 'Untitled profile', description: null, line, corridor_m: 30, ve: 1, labels: [],
  options: { colour: 'view', strata: true, auto: true, legend: true }, visibility: 'members', can_edit: true, can_label: canWrite() });
const uid = () => Math.random().toString(36).slice(2, 10);

function setUI(patchObj) { profileUI.value = { ...profileUI.value, ...patchObj }; }
function setDoc(d) { setUI({ doc: d }); viewer?.setProfileLine(d?.line?.length > 1 ? { line: d.line, corridor: d.corridor_m } : null); }

// ------------------------------------------------------------------ opening, drawing, closing
export function openProfiles() {
  const u = profileUI.value;
  if (u.open) return;
  setUI({ open: true });
  if (!u.doc) startDrawing();
  else viewer.setProfileLine({ line: u.doc.line, corridor: u.doc.corridor_m });
}

export function closeProfiles() {
  viewer.cancelProfileLine(); viewer.setProfileLine(null); viewer.setProfileCursor(null);
  setUI({ open: false, drawing: false });
}

function startDrawing(keep) {
  const u = profileUI.value;
  setUI({ drawing: true, draft: keep ? u.doc?.line || [] : [] });
  viewer.setProfileLine(null);
  viewer.startProfileLine(keep ? u.doc?.line : [], {
    change: (pts) => setUI({ draft: pts }),
    done: (pts) => finishDrawing(pts),
  });
}

function finishDrawing(pts) {
  const u = profileUI.value;
  if (!pts || pts.length < 2) { setUI({ drawing: false }); if (u.doc) viewer.setProfileLine({ line: u.doc.line, corridor: u.doc.corridor_m }); else notify('A profile line needs at least two points', 'err'); return; }
  pts = pts.map(([e, n]) => [Math.round(e * 100) / 100, Math.round(n * 100) / 100]);
  const d = u.doc && (u.doc.can_edit || !u.doc.id) ? { ...u.doc, line: pts } : newDoc(pts);
  setUI({ drawing: false, fit: (u.fit || 0) + 1, ...(d === u.doc ? {} : { saved: null }) });
  setDoc(d);
}

async function openSaved(id) {
  try {
    const d = await get(`/api/profiles/${id}`);
    d.options = { colour: 'view', strata: true, auto: true, legend: true, ...(d.options || {}) };
    viewer.cancelProfileLine();
    setUI({ drawing: false, saved: snapshot(d), fit: (profileUI.value.fit || 0) + 1 });
    setDoc(d);
    viewer.flyToLocs(d.line.map(([e, n]) => ({ easting: e, northing: n, on_surface: true })));
  } catch (e) { notify(e.message, 'err'); }
}

// ------------------------------------------------------------------ the HUD button
export function ProfileButton() {
  const u = profileUI.value;
  return html`<button class=${'btn sm' + (u.open ? ' primary' : '')} style="font-size:12px;padding:3px 8px"
    onClick=${() => (u.open ? closeProfiles() : openProfiles())}
    title="Draw a line across the map for a vertical profile of the ground, the rock and the passages along it">〰 Profile line</button>`;
}

// ------------------------------------------------------------------ the panel
export function ProfilePanel() {
  const u = profileUI.value, d = u.doc;
  const [height, setHeight] = useState(() => { try { return +localStorage.getItem('atlas.profile.h') || 330; } catch { return 330; } });
  const [list, setList] = useState(null);
  const [ground, setGround] = useState(null);
  const [addMode, setAddMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const plot = useRef();
  const heightRef = useRef(height); heightRef.current = height;

  useEffect(() => {
    document.body.classList.toggle('has-profile', !!u.open);
    document.body.classList.toggle('pf-drawing', !!u.drawing);
    document.body.style.setProperty('--profile-h', `${u.drawing ? 104 : height}px`);
  }, [u.open, height, u.drawing]);
  useEffect(() => {                                     // keyboard while drawing
    if (!u.drawing) return;
    const k = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.key === 'Enter') { e.preventDefault(); viewer.finishProfileLine(); }
      else if (e.key === 'Backspace') { e.preventDefault(); viewer.undoProfilePoint(); }
      else if (e.key === 'Escape') { viewer.cancelProfileLine(); finishDrawing(null); }
    };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [u.drawing]);
  const lineKey = d ? JSON.stringify(d.line) : '';
  useEffect(() => {                                     // ground along the line
    if (!d?.line || d.line.length < 2) { setGround(null); return; }
    let live = true;
    const L = new Polyline(d.line).L;
    post('/api/profiles/ground', { line: d.line, step: Math.max(1, L / 3000) })
      .then(g => { if (live) setGround(g); }).catch(e => notify(e.message, 'err'));
    return () => { live = false; };
  }, [lineKey]);
  useEffect(() => { if (u.open && list === null) get('/api/profiles').then(setList).catch(() => setList([])); }, [u.open]);
  const planOn = d?.options?.plan === 'above' || d?.options?.plan === 'below';
  useEffect(() => { if (planOn && !u.drawing && heightRef.current < 440) setHeight(Math.min(440, window.innerHeight - 140)); }, [planOn, d?.id]);

  if (!u.open) return null;
  const dirty = d && snapshot(d) !== u.saved;
  const canSaveAll = d && canWrite() && (!d.id || d.can_edit);
  const canSaveLabels = d && d.id && !d.can_edit && d.can_label;
  const update = (p) => setDoc({ ...d, ...p });
  const setOpt = (p) => update({ options: { ...d.options, ...p } });

  async function save(asNew) {
    if (!d) return;
    setSaving(true);
    try {
      const body = JSON.parse(snapshot(d));
      let r;
      if (canSaveLabels && !asNew) r = await api(`/api/profiles/${d.id}/labels`, { method: 'PUT', body: { labels: d.labels } });
      else if (d.id && !asNew) r = await patch(`/api/profiles/${d.id}`, body);
      else r = await post('/api/profiles', asNew ? { ...body, name: `${d.name} (copy)` } : body);
      r.options = { ...d.options, ...(r.options || {}) };
      setUI({ saved: snapshot(r) }); setDoc(r);
      notify(asNew ? 'Saved as a new profile' : 'Profile saved');
      get('/api/profiles').then(setList).catch(() => {});
    } catch (e) { notify(e.message, 'err'); }
    setSaving(false);
  }
  function clearAll() {                                 // start again: nothing is saved or deleted
    setAddMode(false); setUI({ doc: null, saved: null }); viewer.setProfileLine(null); viewer.setProfileCursor(null); startDrawing();
  }
  async function remove() {
    try { await del(`/api/profiles/${d.id}`); notify('Profile deleted'); setUI({ doc: null, saved: null }); viewer.setProfileLine(null);
      get('/api/profiles').then(setList).catch(() => {}); startDrawing(); } catch (e) { notify(e.message, 'err'); }
  }

  const resize = (e) => {
    e.preventDefault();
    const y0 = e.clientY, h0 = height;
    const mv = (ev) => setHeight(Math.max(180, Math.min(window.innerHeight - 140, h0 - (ev.clientY - y0))));
    const up = () => { window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', up);
      try { localStorage.setItem('atlas.profile.h', String(Math.round(heightRef.current))); } catch { /* private mode */ } };
    window.addEventListener('pointermove', mv); window.addEventListener('pointerup', up);
  };
  const draft = u.draft || [];
  const draftLen = draft.length > 1 ? new Polyline(draft).L : 0;

  return html`<section class="profile-panel" style=${u.drawing ? 'height:auto' : `height:${height}px`} aria-label="Profile along a line">
    <div class="profile-grip" onPointerDown=${resize} title="Drag to resize"></div>
    <header>
      ${d && !u.drawing ? html`<input class="pf-name" value=${d.name} aria-label="Profile name" disabled=${!canSaveAll}
          onInput=${(e) => update({ name: e.target.value })} />`
        : html`<b style="margin-right:6px">Profile along a line</b>`}
      ${d && !u.drawing && html`<span class="tiny muted num">${fmtLen(new Polyline(d.line).L).split(' (')[0]} · ${d.line.length - 1} leg${d.line.length > 2 ? 's' : ''}</span>`}
      <span style="flex:1"></span>
      ${!u.drawing && html`
        <label class="pf-ctl" title="Passages this far either side of the line are swung onto it">Corridor ±
          <input type="number" min="1" max="500" step="5" value=${d?.corridor_m ?? 30} disabled=${!d}
            onChange=${(e) => update({ corridor_m: Math.max(1, Math.min(500, +e.target.value || 30)) })} /> m</label>
        <label class="pf-ctl">VE <select value=${d?.ve ?? 1} disabled=${!d} onChange=${(e) => update({ ve: +e.target.value })}>
          ${VES.map(v => html`<option value=${v}>${v}×</option>`)}</select></label>
        <label class="pf-ctl">Passages <select value=${d?.options?.colour || 'view'} disabled=${!d} onChange=${(e) => setOpt({ colour: e.target.value })}>
          <option value="view">3D colours</option><option value="cave">by cave</option></select></label>
        ${geology.value && html`<label class="pf-ctl"><input type="checkbox" checked=${d?.options?.strata !== false} disabled=${!d}
          onChange=${(e) => setOpt({ strata: e.target.checked })} /> Rock</label>`}
        <label class="pf-ctl"><input type="checkbox" checked=${d?.options?.auto !== false} disabled=${!d}
          onChange=${(e) => setOpt({ auto: e.target.checked })} /> Names</label>
        <label class="pf-ctl" title="A plan of the passages along the line, at the same scale, above or below the profile">Plan
          <select value=${d?.options?.plan || 'off'} disabled=${!d} onChange=${(e) => { setOpt({ plan: e.target.value });
          }}>
            <option value="off">off</option><option value="above">above</option><option value="below">below</option></select></label>
        ${d?.options?.plan && d.options.plan !== 'off' && html`<label class="pf-ctl" title="Lines joining labelled features and entrances in the plan to the profile">
          <input type="checkbox" checked=${d.options.ties !== false} onChange=${(e) => setOpt({ ties: e.target.checked })} /> Tie lines</label>`}
        <button class=${'btn sm' + (addMode ? ' primary' : '')} disabled=${!d} onClick=${() => setAddMode(!addMode)}
          title="Then click a feature (on the profile, the plan or the 3D map) to label it with its name, or anywhere for a blank label">＋ Label</button>
        <button class="btn sm" disabled=${!d} onClick=${() => setUI({ fit: (u.fit || 0) + 1 })} title="Fit the whole profile">Fit</button>
        <${ExportMenu} plot=${plot} doc=${d} />
        <${OpenMenu} list=${list} current=${d?.id} />
        ${(canSaveAll || canSaveLabels) && html`<button class=${'btn sm' + (dirty ? ' primary' : '')} disabled=${!d || saving || (!dirty && d.id)}
          onClick=${() => save(false)} title=${canSaveLabels ? 'Save your labels on this profile' : 'Save this profile for everyone'}>${saving ? 'Saving…' : d?.id ? (dirty ? 'Save' : 'Saved') : 'Save'}</button>`}
        ${d?.id && canWrite() && html`<button class="btn sm" onClick=${() => save(true)} title="Save a copy you can change">Copy</button>`}
        ${(!d || canSaveAll || !d.id) && html`<button class="btn sm" onClick=${() => startDrawing(!!d)} title=${d ? 'Change the line: click to add points, Backspace to remove the last' : 'Draw a line'}>
          ${d ? 'Edit line' : 'Draw line'}</button>`}
        ${d && (dirty || !d.id ? html`<${ConfirmButton} label="Clear" cls="btn sm" confirm=${d.id ? 'Discard your changes and start again?' : 'Clear this profile and its labels?'} onConfirm=${clearAll} />`
          : html`<button class="btn sm" onClick=${clearAll} title="Put this profile away and draw a new line (it stays saved)">Clear</button>`)}
        ${d?.id && d.can_edit && html`<${ConfirmButton} label="Delete" confirm="Delete this profile for everyone?" onConfirm=${remove} />`}`}
      <button class="btn ghost sm" onClick=${closeProfiles} aria-label="Close the profile">✕</button>
    </header>
    ${u.drawing ? html`<div class="pf-draw">
        <div><b>Draw the line on the map.</b> Click along the ground; click on or near a passage to snap to its survey station, so the line can
          follow a trunk passage. Double-click or press Enter to finish, Backspace removes the last point, Esc cancels.</div>
        <div class="row" style="justify-content:flex-start;gap:8px;margin-top:8px">
          <span class="small num">${draft.length} point${draft.length === 1 ? '' : 's'}${draftLen ? ` · ${fmtLen(draftLen)}` : ''}</span>
          <button class="btn sm primary" disabled=${draft.length < 2} onClick=${() => viewer.finishProfileLine()}>Done</button>
          <button class="btn sm" disabled=${!draft.length} onClick=${() => viewer.undoProfilePoint()}>Undo</button>
          <button class="btn sm" onClick=${() => { viewer.cancelProfileLine(); finishDrawing(null); }}>Cancel</button>
          ${list?.length > 0 && !d && html`<span class="small muted">or open a saved one:</span><${OpenMenu} list=${list} />`}</div></div>`
      : d ? html`<${ProfilePlot} doc=${d} ground=${ground} plotRef=${plot} addMode=${addMode} setAddMode=${setAddMode}
          canLabel=${!d.id || d.can_label || d.can_edit || !canWrite()} onDoc=${update} fitKey=${u.fit || 0} />`
        : html`<div class="pf-draw muted small">No profile yet.</div>`}
  </section>`;
}

function OpenMenu({ list, current }) {
  if (!list?.length) return null;
  return html`<select class="pf-open" value="" aria-label="Open a saved profile" onChange=${(e) => { if (e.target.value) openSaved(+e.target.value); e.target.value = ''; }}>
    <option value="">Open saved…</option>
    ${list.map(p => html`<option value=${p.id} disabled=${p.id === current}>${p.name} · ${fmtLen(p.length_m).split(' (')[0]}${p.created_by_name ? ` · ${p.created_by_name}` : ''}</option>`)}</select>`;
}

function ExportMenu({ plot, doc }) {
  const name = (doc?.name || 'profile').replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '_') || 'profile';
  const go = async (kind) => {
    const svg = plot.current?.querySelector('svg.pf-svg'); if (!svg) return;
    const text = svgText(svg);
    if (kind === 'svg') return saveBlob(`${name}.svg`, new Blob([text], { type: 'image/svg+xml' }));
    const img = new Image(), w = svg.clientWidth, h = svg.clientHeight, k = 2;
    img.onload = () => {
      const c = document.createElement('canvas'); c.width = w * k; c.height = h * k;
      const g = c.getContext('2d'); g.scale(k, k); g.drawImage(img, 0, 0, w, h);
      c.toBlob(b => saveBlob(`${name}.png`, b), 'image/png');
    };
    img.onerror = () => notify('Could not make the picture', 'err');
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(text);
  };
  return html`<select class="pf-open" value="" disabled=${!doc} aria-label="Export" onChange=${(e) => { if (e.target.value) go(e.target.value); e.target.value = ''; }}>
    <option value="">Export…</option><option value="png">Picture (PNG)</option><option value="svg">Drawing (SVG, editable)</option></select>`;
}

function svgText(svg) {
  const c = svg.cloneNode(true);
  c.setAttribute('xmlns', 'http://www.w3.org/2000/svg'); c.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  c.setAttribute('width', svg.clientWidth); c.setAttribute('height', svg.clientHeight);
  c.querySelectorAll('[data-noexport]').forEach(n => n.remove());
  return '<?xml version="1.0" encoding="UTF-8"?>\n' + new XMLSerializer().serializeToString(c);
}

function saveBlob(name, blob) {
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name;
  document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
}

// ------------------------------------------------------------------ the drawing
function niceStep(range, n) {
  const raw = range / Math.max(1, n), p = 10 ** Math.floor(Math.log10(raw)), f = raw / p;
  return (f < 1.5 ? 1 : f < 3.5 ? 2 : f < 7.5 ? 5 : 10) * p;
}

function groundAtS(G, s) {
  if (!G) return null;
  const S = G.s, Z = G.z; let lo = 0, hi = S.length - 1;
  if (s <= S[0]) return Z[0]; if (s >= S[hi]) return Z[hi];
  while (hi - lo > 1) { const m = (lo + hi) >> 1; if (S[m] <= s) lo = m; else hi = m; }
  const a = Z[lo], b = Z[hi]; if (a == null || b == null) return a ?? b;
  return a + (b - a) * (s - S[lo]) / ((S[hi] - S[lo]) || 1);
}

const PICK_COL = { entrance: '#0f8a5f', station: '#5d5a52', place: '#1f6fd1', maptext: '#9a5a1c', lead: '#c0392b', height: '#1f6fd1',
  drop: '#c0392b', cave: '#d95926', rock: '#7d6b4a', feature: '#0f8a5f', observation: '#b26b00' };
const PICK_NAME = { entrance: 'entrance', station: 'station', place: 'named place', maptext: 'map label', lead: 'lead', height: 'ceiling height',
  drop: 'pit', cave: 'cave', rock: 'rock unit', feature: 'surface feature', observation: 'observation' };
let READINGS = null;                                     // heights and pits read off the maps (fetched once)

function ProfilePlot({ doc, ground, plotRef, addMode, setAddMode, canLabel, onDoc, fitKey }) {
  const box = useRef();
  const [size, setSize] = useState({ w: 800, h: 260 });
  const [view, setView] = useState(null);               // {s0, kx, zTop}
  const [hover, setHover] = useState(null);
  const [pick, setPick] = useState(null);                // the feature under the cursor while adding a label
  const [edit, setEdit] = useState(null);                // label id being edited
  const [readings, setReadings] = useState(READINGS);
  const drag = useRef(null);
  const o = doc.options || {}, planMode = o.plan === 'above' || o.plan === 'below' ? o.plan : null;
  const M = { ...M0, t: o.legend !== false && viewer?.geo && o.strata !== false ? 48 : M0.t };
  const W = size.w, H = size.h, pw = W - M.l - M.r, avail = H - M.t - M.b;
  // the profile (PT, PH) and the plan strip (QT, QH) share the width and the distance-along-the-line scale
  const gap = 12, QH = planMode ? Math.max(56, Math.min(420, Math.round(avail * 0.4))) : 0;
  const PT = planMode === 'above' ? M.t + QH + gap : M.t, PH = Math.max(40, avail - (planMode ? QH + gap : 0));
  const QT = planMode === 'above' ? M.t : PT + PH + gap, QC = QT + QH / 2;
  const ve = doc.ve || 1, corridor = doc.corridor_m || 30;
  const line = useMemo(() => new Polyline(doc.line), [JSON.stringify(doc.line)]);
  const mdl = model.value, op = opts.value, flt = filter.value, G = geology.value;
  const docRef = useRef(doc); docRef.current = doc;

  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setSize({ w: Math.max(200, e.contentRect.width), h: Math.max(120, e.contentRect.height) }));
    ro.observe(box.current); return () => ro.disconnect();
  }, []);

  const pass = useMemo(() => (viewer?.M ? passagesNear(viewer, line, corridor, o.colour || 'view') : { quads: [], cl: [], stations: [], caves: [] }),
    [line, corridor, o.colour, mdl, op.shift, op.colorBy, flt, viewer?.tcol]);
  const half = Math.max(120, Math.min(600, corridor * 3));
  const plan = useMemo(() => (planMode && viewer?.M ? planNear(viewer, line, half, o.colour || 'view') : null),
    [planMode, line, half, o.colour, mdl, op.shift, op.colorBy, flt, viewer?.tcol]);
  if (typeof window !== 'undefined' && location.search.includes('debug')) window.__pf = { pass, line, plan };
  const ents = useMemo(() => entrancesNear(places.value, line, corridor, (E, N) => viewer.groundAt(E, N)), [line, corridor, places.value]);
  const ext = useMemo(() => {                            // data extent
    let z0 = Infinity, z1 = -Infinity;
    for (const z of ground?.z || []) if (z != null) { z0 = Math.min(z0, z); z1 = Math.max(z1, z); }
    for (const q of pass.quads) { z0 = Math.min(z0, q.b0, q.b1); z1 = Math.max(z1, q.t0, q.t1); }
    if (!Number.isFinite(z0)) { z0 = 0; z1 = 100; }
    return { z0, z1 };
  }, [ground, pass]);
  const [strata, setStrata] = useState(null);
  const strataKey = view && ground ? [Math.round(view.s0 * 10), Math.round(view.kx * 1e4), Math.round(view.zTop * 10), ve, W, PH].join(',') : '';
  useEffect(() => {                                     // the rock under the visible part, at screen resolution
    if (!ground || !view || !viewer?.geo || o.strata === false) { setStrata(null); return; }
    const t = setTimeout(() => {
      const g = viewer.geo, k = view.kx, kyy = k * ve;
      const a = Math.max(0, view.s0), b = Math.min(line.L, view.s0 + pw / k);
      let gmax = -Infinity; for (const z of ground.z) if (z != null) gmax = Math.max(gmax, z);
      const zhi = Math.min(view.zTop, gmax + 2), zlo = Math.max(g.zmin, view.zTop - PH / kyy);
      if (b <= a || zhi <= zlo) { setStrata(null); return; }
      const cols = (viewer.C?.units || g.colors).map(c => '#' + c.getHexString());
      const w = (b - a) * k, h = (zhi - zlo) * kyy, f = Math.min(1, Math.sqrt(9e5 / Math.max(1, w * h)));
      setStrata(strataImage(g, line, ground.s, ground.z, zlo, zhi, cols, { s0: a, s1: b, W: w * f, H: h * f }));
    }, strata ? 120 : 0);
    return () => clearTimeout(t);
  }, [strataKey, ground, line, G, o.strata, viewer?.C]);

  // what a label can be made from (only worked out while adding one)
  useEffect(() => {
    if (!addMode || readings || !feat('xsections')) return;
    get('/api/map-readings').then(R => { READINGS = R.sheets.flatMap(s => s.marks.filter(m => !m.off)); setReadings(READINGS); }).catch(() => {});
  }, [addMode]);
  const feats = useMemo(() => {
    if (!addMode || !viewer?.M) return [];
    const near = Math.max(corridor, 60);
    const F = featuresNear(viewer, line, near, { places: places.value.filter(p => p.type === 'feature' || p.type === 'observation'),
      cavePlaces: cavePlaces.value.map(c => ({ ...c, label: placeLabel(c) })), readings: readings || [], groundAt: (E, N) => viewer.groundAt(E, N) });
    for (const st of pass.stations) F.push({ kind: 'station', text: st.name, s: st.s, d: st.sd, z: st.z });
    for (const c of pass.caves) F.push({ kind: 'cave', text: c.name, s: (c.s0 + c.s1) / 2, d: null, z: c.top });
    if (strata) for (const L of strata.labels) { const u = viewer.geo.units[L.unit]; if (u) F.push({ kind: 'rock', text: u.name, s: L.s, d: null, z: L.z }); }
    return F;
  }, [addMode, line, corridor, readings, places.value, cavePlaces.value, pass, strata]);

  const addLabel = (f) => {                              // a label made from a feature, or a blank one
    const d = docRef.current;
    const l = { id: uid(), s: Math.round(f.s * 10) / 10, z: Math.round(f.z * 10) / 10, text: f.text || 'Label', color: INK };
    if (f.d != null && Number.isFinite(f.d)) { l.d = Math.round(f.d * 10) / 10; l.tie = true; }
    if (f.kind) l.kind = f.kind;
    onDoc({ labels: [...(d.labels || []), l] }); setEdit(f.kind ? null : l.id); setAddMode(false); setPick(null);
    if (f.kind) notify(`Label added: ${l.text}. Click it to change it.`);
  };
  useEffect(() => {                                     // clicking something on the 3D map while adding a label
    if (!viewer || !addMode) return;
    document.body.classList.add('pf-labelpick');
    viewer.labelPick = (h) => {
      if (h.E == null || h.N == null) return;
      const p = line.project(h.E, h.N);
      if (!p) { notify(`${h.text} is beyond the ends of the line`, 'err'); return; }
      if (p.d > Math.max(corridor * 3, 100)) notify(`${h.text} is ${Math.round(p.d)} m from the line`);
      addLabel({ kind: h.kind, text: h.text, s: p.s, d: p.d * p.side, z: Number.isFinite(h.Z) ? h.Z : groundAtS(ground, p.s) ?? 0 });
    };
    return () => { viewer.labelPick = null; document.body.classList.remove('pf-labelpick'); };
  }, [addMode, line, corridor, ground]);

  const fit = () => {
    const zr = Math.max(10, ext.z1 - ext.z0) * 1.25;
    const kx = Math.min(pw / (line.L * 1.04), PH / (zr * ve));
    const zc = (ext.z0 + ext.z1) / 2;
    setView({ s0: line.L / 2 - pw / kx / 2, kx, zTop: zc + PH / 2 / (kx * ve) });
  };
  useEffect(() => { if (ground) fit(); }, [fitKey, !!ground, W > 0 && Math.round(W / 50), Math.round(H / 50), planMode]);
  const veRef = useRef(ve);
  useEffect(() => {                                       // keep the middle height in place when VE changes
    const old = veRef.current; veRef.current = ve;
    setView(v => { if (!v || old === ve) return v; const zc = v.zTop - PH / 2 / (v.kx * old); return { ...v, zTop: zc + PH / 2 / (v.kx * ve) }; });
  }, [ve]);
  useEffect(() => () => viewer?.setProfileCursor(null), []);
  const wheelRef = useRef();
  useEffect(() => {                                        // wheel zoom needs a non-passive listener
    const el = box.current; if (!el) return;
    const f = (e) => { if (e.target.closest?.('svg') && wheelRef.current) wheelRef.current(e); };
    el.addEventListener('wheel', f, { passive: false }); return () => el.removeEventListener('wheel', f);
  }, []);

  if (!view || !ground) return html`<div class="pf-plot" ref=${box}><div class="pf-draw muted small">Reading the ground along the line…</div></div>`;
  const kx = view.kx, ky = kx * ve;
  const X = (s) => M.l + (s - view.s0) * kx, Y = (z) => PT + (view.zTop - z) * ky;
  const S_ = (x) => view.s0 + (x - M.l) / kx, Z_ = (y) => view.zTop - (y - PT) / ky;
  const PY = (d) => QC + d * kx, D_ = (y) => (y - QC) / kx;          // the plan: d metres right of the line, downwards
  const sMin = S_(M.l), sMax = S_(M.l + pw), zMax = Z_(PT), zMin = Z_(PT + PH);
  const inS = (s, pad = 0) => s >= sMin - pad && s <= sMax + pad;

  // ---- paths
  const gpts = []; for (let i = 0; i < ground.s.length; i++) if (ground.z[i] != null) gpts.push(`${X(ground.s[i]).toFixed(1)},${Y(ground.z[i]).toFixed(1)}`);
  const gPath = gpts.length ? 'M' + gpts.join('L') : '';
  const groups = new Map();
  for (const q of pass.quads) {
    if (Math.max(q.s0, q.s1) < sMin - 5 || Math.min(q.s0, q.s1) > sMax + 5) continue;
    const f = q.dim ? 0.25 : q.d < corridor / 3 ? 0.95 : q.d < corridor * 2 / 3 ? 0.7 : 0.45, col = q.dim ? '#8c8b82' : q.col;
    const key = col + '|' + f;
    let t0 = Y(q.t0), b0 = Y(q.b0), t1 = Y(q.t1), b1 = Y(q.b1);
    if (b0 - t0 < 3) { const m = (t0 + b0) / 2; t0 = m - 1.5; b0 = m + 1.5; } if (b1 - t1 < 3) { const m = (t1 + b1) / 2; t1 = m - 1.5; b1 = m + 1.5; }
    const x0 = X(q.s0), x1 = X(q.s1);
    const seg = `M${x0.toFixed(1)},${t0.toFixed(1)}L${x1.toFixed(1)},${t1.toFixed(1)}L${x1.toFixed(1)},${b1.toFixed(1)}L${x0.toFixed(1)},${b0.toFixed(1)}Z`;
    groups.set(key, (groups.get(key) || '') + seg);
  }
  let cl = '';
  for (const [s0, z0, s1, z1] of pass.cl) { if (Math.max(s0, s1) < sMin || Math.min(s0, s1) > sMax) continue; cl += `M${X(s0).toFixed(1)},${Y(z0).toFixed(1)}L${X(s1).toFixed(1)},${Y(z1).toFixed(1)}`; }

  // ---- axes
  const zs = niceStep((zMax - zMin), PH / 46), ss = niceStep((sMax - sMin), pw / 110);
  const zTicks = []; for (let z = Math.ceil(zMin / zs) * zs; z <= zMax; z += zs) zTicks.push(z);
  const ftMin = zMin / FT, ftMax = zMax / FT, fs = niceStep(ftMax - ftMin, PH / 46);
  const fTicks = []; for (let f = Math.ceil(ftMin / fs) * fs; f <= ftMax; f += fs) fTicks.push(f);
  const sTicks = []; for (let s = Math.max(0, Math.ceil(sMin / ss) * ss); s <= Math.min(line.L, sMax); s += ss) sTicks.push(s);
  const bar = niceStep(pw / kx, 4);                              // scale bar length (m)
  const brg = line.bearings();
  const axisY = M.t + avail;                                     // the distance axis under whichever panel is lowest

  // ---- labels
  const lbl = (x, y, text, { size = 11, color = INK, anchor = 'start', weight = 600, italic = false, halo = PAPER } = {}) => html`
    <text x=${x} y=${y} font-size=${size} font-weight=${weight} font-style=${italic ? 'italic' : 'normal'} fill=${color} text-anchor=${anchor}
      stroke=${halo} stroke-width="3" stroke-linejoin="round" paint-order="stroke" font-family="system-ui, sans-serif">${text}</text>`;
  const autos = [];
  const cx = (x, text, size) => { const w = text.length * size * 0.56; return Math.max(M.l + w / 2 + 4, Math.min(M.l + pw - w / 2 - 4, x)); };
  if (o.auto !== false) {
    if (strata) for (const L of strata.labels) {
      if (L.thick * ky < 16 || L.s < sMin || L.s > sMax) continue;
      const u = viewer.geo.units[L.unit]; if (!u) continue;
      autos.push(lbl(cx(X(L.s), u.name, 11), Y(L.z) + 4, u.name, { size: 11, italic: true, weight: 500, color: '#2d2b27', anchor: 'middle', halo: 'rgba(247,244,236,.75)' }));
    }
    for (const c of pass.caves.slice(0, 8)) {
      const s = (c.s0 + c.s1) / 2; if (s < sMin || s > sMax) continue;
      const gz = groundAtS(ground, s);
      autos.push(lbl(cx(X(s), c.name, 12), Math.max(PT + 28, Math.min(Y(c.top) - 8, gz != null ? Y(gz) - 22 : 1e9)), c.name, { size: 12, color: o.colour === 'cave' ? c.color : INK, anchor: 'middle', weight: 700 }));
    }
  }
  const entMark = (x, y, name, key) => html`<g key=${key}>
    <circle cx=${x} cy=${y} r="6" fill="#199e70" stroke=${PAPER} stroke-width="1.5" />
    <text x=${x} y=${y + 3.5} font-size="8" font-weight="700" fill="#fff" text-anchor="middle" font-family="system-ui, sans-serif">E</text>
    ${o.auto !== false && name && lbl(x + 8, y - 8, name, { size: 11, color: '#0b6b4b' })}</g>`;
  const entMarks = ents.filter(e => inS(e.s)).map(e => entMark(X(e.s), Y(e.z), e.name, 'p' + e.id));

  // ---- the plan strip
  const ties = o.ties !== false;
  let planEls = null, tieEls = null;
  if (planMode && plan) {
    const pgroups = new Map();
    for (const q of plan.polys) {
      if (!q.pts.length || !q.pts.some(([s]) => inS(s, 5))) continue;
      const col = q.dim ? '#8c8b82' : q.col, key = col + '|' + (q.dim ? 0.25 : 0.6);
      pgroups.set(key, (pgroups.get(key) || '') + 'M' + q.pts.map(([s, d]) => `${X(s).toFixed(1)},${PY(d).toFixed(1)}`).join('L') + 'Z');
    }
    const segs = (L) => { let p = ''; for (const [s0, d0, s1, d1] of L) { if (!inS(s0, 2) && !inS(s1, 2)) continue; p += `M${X(s0).toFixed(1)},${PY(d0).toFixed(1)}L${X(s1).toFixed(1)},${PY(d1).toFixed(1)}`; } return p; };
    const wallP = segs(plan.walls), detP = segs(plan.detail), clP = segs(plan.cl);
    const texts = [];
    if (o.auto !== false && kx > 0.5) {
      const taken = [];
      for (const t of plan.texts) {
        if (!inS(t.s) || t.text.length > 40) continue;
        const x = X(t.s), y = PY(t.d), w = t.text.length * 5.4;
        if (y < QT + 8 || y > QT + QH - 4) continue;
        const b = [x - w / 2, y - 9, x + w / 2, y + 3];
        if (taken.some(c => b[0] < c[2] && b[2] > c[0] && b[1] < c[3] && b[3] > c[1])) continue;
        taken.push(b);
        texts.push(lbl(x, y, t.text, { size: 9.5, italic: true, weight: 600, anchor: 'middle', color: t.kind === 'lead' ? '#c0392b' : '#7a4a1c' }));
      }
    }
    const norths = line.seg.map((g, i) => {             // north on each leg (the strip turns with the line)
      const a = X(g.s0), b = X(g.s0 + g.L), xm = (Math.max(a, M.l) + Math.min(b, M.l + pw)) / 2;
      if (Math.min(b, M.l + pw) - Math.max(a, M.l) < 46) return null;
      return html`<g transform=${`translate(${xm.toFixed(1)},${(QT + 17).toFixed(1)}) rotate(${(90 - brg[i]).toFixed(1)})`}>
        <path d="M0,-10 L4,4 L0,1.5 L-4,4 Z" fill=${INK2} /><text y="-12" font-size="8" font-weight="700" fill=${INK2} text-anchor="middle" font-family="system-ui, sans-serif">N</text></g>`;
    });
    planEls = html`<g>
      <rect x=${M.l} y=${QT} width=${pw} height=${QH} fill="#fbf9f3" />
      <g clip-path="url(#pf-qclip)">
        <rect x=${Math.max(M.l, X(0))} y=${PY(-corridor)} width=${Math.max(0, Math.min(M.l + pw, X(line.L)) - Math.max(M.l, X(0)))} height=${2 * corridor * kx} fill=${LINE_COL} fill-opacity="0.06" />
        ${[...pgroups.entries()].map(([k, d]) => { const [col, f] = k.split('|'); return html`<path d=${d} fill=${col} fill-opacity=${f} stroke="none" />`; })}
        ${detP && html`<path d=${detP} stroke="#9a958a" stroke-width="0.7" fill="none" />`}
        ${wallP && html`<path d=${wallP} stroke=${INK} stroke-width="1.1" fill="none" stroke-linecap="round" />`}
        ${clP && html`<path d=${clP} stroke="#2a2723" stroke-width="0.7" fill="none" opacity="0.5" />`}
        <line x1=${Math.max(M.l, X(0))} x2=${Math.min(M.l + pw, X(line.L))} y1=${QC} y2=${QC} stroke=${LINE_COL} stroke-width="1.6" />
        ${[-corridor, corridor].map(d => html`<line x1=${Math.max(M.l, X(0))} x2=${Math.min(M.l + pw, X(line.L))} y1=${PY(d)} y2=${PY(d)} stroke=${LINE_COL} stroke-width="0.8" stroke-dasharray="5 4" opacity="0.6" />`)}
        ${line.corners.slice(1, -1).map(s => inS(s) && html`<line x1=${X(s)} x2=${X(s)} y1=${QT} y2=${QT + QH} stroke=${LINE_COL} stroke-width="1" stroke-dasharray="4 4" opacity="0.5" />`)}
        ${texts}
        ${ents.filter(e => inS(e.s)).map(e => entMark(X(e.s), PY(e.sd), e.name, 'q' + e.id))}
        ${(doc.labels || []).filter(l => l.d != null && inS(l.s)).map(l => html`<circle cx=${X(l.s)} cy=${PY(l.d)} r="3.2" fill=${l.color || INK} stroke=${PAPER} stroke-width="1" />`)}
        ${norths}
      </g>
      <rect x=${M.l} y=${QT} width=${pw} height=${QH} fill="none" stroke="#bdb6a5" stroke-width="1" />
      ${lbl(M.l - 7, QT + 12, 'PLAN', { size: 9.5, color: INK2, anchor: 'end', weight: 700 })}
      ${lbl(M.l - 7, QT + 24, 'left ↑', { size: 9, color: INK2, anchor: 'end', weight: 500 })}
      ${lbl(M.l - 7, QT + QH - 5, 'right ↓', { size: 9, color: INK2, anchor: 'end', weight: 500 })}
      ${QH > 90 && lbl(M.l + pw + 7, QT + 12, 'true', { size: 9, color: INK2, weight: 500 })}
      ${QH > 90 && lbl(M.l + pw + 7, QT + 23, 'scale', { size: 9, color: INK2, weight: 500 })}
    </g>`;
    if (ties) {
      const clampQ = (y) => Math.max(QT, Math.min(QT + QH, y)), clampP = (y) => Math.max(PT, Math.min(PT + PH, y));
      const tie = (s, d, z, col, key) => inS(s) && html`<line key=${key} x1=${X(s)} x2=${X(s)} y1=${clampQ(PY(d))} y2=${clampP(Y(z))} stroke=${col} stroke-width="0.9" stroke-dasharray="3 3" opacity="0.7" />`;
      tieEls = html`<g clip-path="url(#pf-aclip)">
        ${(doc.labels || []).filter(l => l.d != null && l.tie !== false).map(l => tie(l.s, l.d, l.z, l.color || INK, l.id))}
        ${ents.map(e => tie(e.s, e.sd, e.z, '#199e70', 'e' + e.id))}</g>`;
    }
  }

  // ---- interaction
  const local = (e) => { const r = box.current.querySelector('svg').getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const inPlan = (y) => planMode && y >= QT && y <= QT + QH;
  const nearestStation = (x, y) => {
    let best = null, bd = 64;
    for (const st of pass.stations) {
      const sy = inPlan(y) ? PY(st.sd) : Y(st.z);
      const d = (X(st.s) - x) ** 2 + (sy - y) ** 2; if (d < bd) { bd = d; best = st; }
    }
    return best;
  };
  const pickAt = (x, y) => {                              // the feature nearest the cursor (profile or plan)
    let best = null, bd = 144;
    for (const f of feats) {
      if (!inS(f.s)) continue;
      const fy = inPlan(y) ? (f.d == null ? null : PY(f.d)) : Y(f.z);
      if (fy == null) continue;
      const d = (X(f.s) - x) ** 2 + (fy - y) ** 2; if (d < bd) { bd = d; best = { ...f, fy }; }
    }
    return best;
  };
  const describe = (x, y) => {
    const s = S_(x);
    if (s < 0 || s > line.L) return null;
    const { E, N, brg: b } = line.at(s), gz = groundAtS(ground, s);
    if (inPlan(y)) {
      const d = D_(y), r = b * Math.PI / 180;
      const E2 = E + Math.cos(r) * d, N2 = N - Math.sin(r) * d;
      return { x, y, s, plan: true, d, E: E2, N: N2, z: viewer.groundAt(E2, N2), st: nearestStation(x, y) };
    }
    const z = Z_(y);
    let pq = null;                                        // passage at this distance nearest the cursor height
    for (const q of pass.quads) {
      const a = Math.min(q.s0, q.s1), bb = Math.max(q.s0, q.s1); if (s < a - 0.5 || s > bb + 0.5) continue;
      const t = bb > a ? (s - q.s0) / (q.s1 - q.s0) : 0.5, top = q.t0 + (q.t1 - q.t0) * t, bot = q.b0 + (q.b1 - q.b0) * t;
      const dz = z > top ? z - top : z < bot ? bot - z : 0;
      if (!pq || dz < pq.dz) pq = { dz, top, bot, cave: q.cave, d: q.d };
    }
    let rock = null;
    if (viewer.geo && gz != null && z <= gz) rock = viewer.geo.describe(viewer.geo.value(E, N, z));
    return { x, y, s, z, E, N, gz, pq, rock, st: nearestStation(x, y) };
  };
  const onDown = (e) => {
    if (e.button !== 0) return;
    const [x, y] = local(e);
    drag.current = { kind: 'pan', x, y, v: view, moved: false, plan: inPlan(y) };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => {
    const [x, y] = local(e), D = drag.current;
    if (D) {
      if (Math.hypot(x - D.x, y - D.y) > 3) D.moved = true;
      if (D.kind === 'pan' && D.moved) setView({ ...D.v, s0: D.v.s0 - (x - D.x) / D.v.kx, zTop: D.plan ? D.v.zTop : D.v.zTop + (y - D.y) / (D.v.kx * ve) });
      if (D.kind === 'label' && D.moved) onDoc({ labels: doc.labels.map(l => (l.id === D.id ? { ...l, s: Math.round(S_(x - D.dx) * 10) / 10, z: Math.round(Z_(y - D.dy) * 10) / 10 } : l)) });
      return;
    }
    if (addMode) setPick(pickAt(x, y));
    const h = describe(x, y); setHover(h);
    if (h) viewer.setProfileCursor(h.E, h.N, h.z); else viewer.setProfileCursor(null);
  };
  const onUp = (e) => {
    const D = drag.current; drag.current = null;
    if (!D || D.moved) return;
    const [x, y] = local(e);
    if (D.kind === 'label') { setEdit(D.id); return; }
    if (addMode && canLabel) {
      const f = pickAt(x, y);
      if (f) addLabel(f);
      else if (!inPlan(y)) addLabel({ s: S_(x), z: Z_(y), text: 'Label' });
      else { const d = D_(y), s = S_(x); addLabel({ s, z: groundAtS(ground, s) ?? 0, d, text: 'Label' }); }
      return;
    }
    const st = nearestStation(x, y);
    if (st) selection.value = viewer.stationInfo(st.i);
  };
  const onWheel = (e) => {
    e.preventDefault();
    setHover(null);
    const [x, y] = local(e), f = Math.exp(-e.deltaY * 0.0015), s = S_(x), yy = inPlan(y) ? PT + PH / 2 : y, z = Z_(yy);
    const k2 = Math.max(0.002, Math.min(200, kx * f));
    setView({ s0: s - (x - M.l) / k2, kx: k2, zTop: z + (yy - PT) / (k2 * ve) });
  };
  wheelRef.current = onWheel;
  const labelDown = (l) => (e) => {
    if (!canLabel || e.button !== 0 || addMode) return;
    e.stopPropagation();
    const [x, y] = local(e);
    drag.current = { kind: 'label', id: l.id, x, y, dx: x - X(l.s), dy: y - Y(l.z), moved: false };
    e.currentTarget.ownerSVGElement.setPointerCapture(e.pointerId);
  };

  const edl = edit && doc.labels.find(l => l.id === edit);
  const units = viewer.geo?.units || [];
  const pickMarks = addMode && html`<g data-noexport="1" pointer-events="none">
    ${feats.filter(f => inS(f.s)).map(f => {
      const c = PICK_COL[f.kind] || INK2, py = Y(f.z), on = pick && pick.kind === f.kind && pick.text === f.text && pick.s === f.s;
      const st = f.kind === 'station' && !on, r = on ? 6 : st ? 2 : 3.4, op = st ? 0.45 : 1;   // stations: many, so faint
      return html`<g opacity=${op}>${py >= PT && py <= PT + PH && html`<circle cx=${X(f.s)} cy=${py} r=${r} fill=${on ? c : 'none'} stroke=${c} stroke-width="1.4" />`}
        ${planMode && f.d != null && PY(f.d) >= QT && PY(f.d) <= QT + QH && html`<circle cx=${X(f.s)} cy=${PY(f.d)} r=${r} fill=${on ? c : 'none'} stroke=${c} stroke-width="1.4" />`}</g>`;
    })}</g>`;
  return html`<div class="pf-plot" ref=${(el) => { box.current = el; if (plotRef) plotRef.current = el; }}>
    <svg class="pf-svg" width=${W} height=${H} viewBox=${`0 0 ${W} ${H}`} style=${`display:block;cursor:${addMode ? (pick ? 'copy' : 'crosshair') : drag.current ? 'grabbing' : 'default'}`}
      onPointerDown=${onDown} onPointerMove=${onMove} onPointerUp=${onUp}
      onPointerLeave=${() => { setHover(null); setPick(null); viewer.setProfileCursor(null); }} role="img"
      aria-label=${`Vertical profile along ${doc.name}`}>
      <rect x="0" y="0" width=${W} height=${H} fill=${PAPER} />
      <defs><clipPath id="pf-clip"><rect x=${M.l} y=${PT} width=${pw} height=${PH} /></clipPath>
        <clipPath id="pf-qclip"><rect x=${M.l} y=${QT} width=${pw} height=${QH} /></clipPath>
        <clipPath id="pf-aclip"><rect x=${M.l} y=${M.t} width=${pw} height=${avail} /></clipPath></defs>
      <g clip-path="url(#pf-clip)">
        ${zTicks.map(z => html`<line x1=${M.l} x2=${M.l + pw} y1=${Y(z)} y2=${Y(z)} stroke="#e4dfd2" stroke-width="1" />`)}
        ${strata ? html`<image href=${strata.url} x=${X(strata.s0)} y=${Y(strata.zhi)} width=${(strata.s1 - strata.s0) * kx} height=${(strata.zhi - strata.zlo) * ky}
            preserveAspectRatio="none" opacity="0.9" />`
          : gPath && html`<path d=${gPath + `L${X(line.L)},${PT + PH + 2000}L${X(0)},${PT + PH + 2000}Z`} fill="#cdbfa5" />`}
        ${line.corners.slice(1, -1).map((s, i) => {
          const x = X(s), gp = Math.min(x - X(line.corners[i]), X(line.corners[i + 2]) - x);
          if (x < M.l - 5 || x > M.l + pw + 5 || gp < 4) return null;
          return html`<g><line x1=${x} x2=${x} y1=${PT} y2=${PT + PH} stroke=${LINE_COL} stroke-width="1" stroke-dasharray="4 4" opacity="0.5" />
            ${gp > 84 && lbl(x + 3, PT + 12, `${Math.round(brg[i])}° → ${Math.round(brg[i + 1])}°`, { size: 9.5, color: '#6d4fc2', weight: 600 })}</g>`; })}
        ${[...groups.entries()].map(([k, d]) => { const [col, f] = k.split('|'); return html`<path d=${d} fill=${col} fill-opacity=${f} stroke=${col} stroke-opacity=${f} stroke-width="0.6" />`; })}
        ${cl && html`<path d=${cl} stroke="#2a2723" stroke-width="0.8" fill="none" opacity="0.55" />`}
        <path d=${gPath} fill="none" stroke=${GROUND} stroke-width="2" stroke-linejoin="round" />
        ${entMarks}
        ${autos}
      </g>
      ${planEls}
      ${tieEls}
      <g clip-path="url(#pf-clip)">
        ${(doc.labels || []).map(l => html`<g style=${canLabel && !addMode ? 'cursor:move' : ''} onPointerDown=${labelDown(l)}>
          <circle cx=${X(l.s)} cy=${Y(l.z)} r="3" fill=${l.color || INK} stroke=${PAPER} stroke-width="1" />
          ${lbl(X(l.s) + 6, Y(l.z) + 4, l.text, { size: 12, color: l.color || INK, weight: 650 })}</g>`)}
        ${hover && !hover.plan && html`<g data-noexport="1" pointer-events="none">
          <line x1=${hover.x} x2=${hover.x} y1=${PT} y2=${PT + PH} stroke=${INK} stroke-width="0.8" opacity="0.5" />
          ${hover.gz != null && html`<line x1=${hover.x} x2=${hover.x} y1=${Y(hover.gz)} y2=${hover.y} stroke="#c98500" stroke-width="2" />`}
          <circle cx=${hover.x} cy=${hover.y} r="3.5" fill="#ffd23f" stroke=${INK} stroke-width="1" /></g>`}
      </g>
      ${hover?.plan && html`<g data-noexport="1" pointer-events="none" clip-path="url(#pf-aclip)">
        <line x1=${hover.x} x2=${hover.x} y1=${M.t} y2=${M.t + avail} stroke=${INK} stroke-width="0.8" opacity="0.5" />
        <circle cx=${hover.x} cy=${hover.y} r="3.5" fill="#ffd23f" stroke=${INK} stroke-width="1" /></g>`}
      ${pickMarks}
      <rect x=${M.l} y=${PT} width=${pw} height=${PH} fill="none" stroke="#bdb6a5" stroke-width="1" />
      ${zTicks.map(z => html`<g><line x1=${M.l - 4} x2=${M.l} y1=${Y(z)} y2=${Y(z)} stroke=${INK2} />
        ${lbl(M.l - 7, Y(z) + 3.5, `${Math.round(z)}`, { size: 10, color: INK2, anchor: 'end', weight: 500 })}</g>`)}
      ${lbl(8, PT - 4, 'm', { size: 10, color: INK2, weight: 600 })}
      ${fTicks.map(f => html`<g><line x1=${M.l + pw} x2=${M.l + pw + 4} y1=${Y(f * FT)} y2=${Y(f * FT)} stroke=${INK2} />
        ${lbl(M.l + pw + 7, Y(f * FT) + 3.5, `${Math.round(f).toLocaleString()}`, { size: 10, color: INK2, weight: 500 })}</g>`)}
      ${lbl(W - 8, PT - 4, 'ft', { size: 10, color: INK2, weight: 600, anchor: 'end' })}
      ${sTicks.map(s => html`<g><line x1=${X(s)} x2=${X(s)} y1=${axisY} y2=${axisY + 4} stroke=${INK2} />
        ${planMode === 'below' && html`<line x1=${X(s)} x2=${X(s)} y1=${PT + PH} y2=${PT + PH + 4} stroke=${INK2} />`}
        ${lbl(X(s), axisY + 15, s >= 1000 && ss >= 100 ? `${(s / 1000).toFixed(ss >= 1000 ? 0 : 1)} km` : `${Math.round(s)}`, { size: 10, color: INK2, anchor: 'middle', weight: 500 })}</g>`)}
      ${lbl(M.l, 16, doc.name, { size: 13, weight: 700 })}
      ${lbl(M.l + pw, 16, `${ve === 1 ? 'no vertical exaggeration' : `vertical exaggeration ${ve}×`} · passages within ±${corridor} m of the line`, { size: 10, color: INK2, weight: 500, anchor: 'end' })}
      ${X(0) >= M.l - 1 && lbl(X(0) + 4, PT + PH - 8, 'A', { size: 14, weight: 800, color: LINE_COL })}
      ${X(line.L) <= M.l + pw + 1 && lbl(X(line.L) - 4, PT + PH - 8, 'A′', { size: 14, weight: 800, color: LINE_COL, anchor: 'end' })}
      <g>
        <rect x=${M.l + 30} y=${PT + PH - 14} width=${bar * kx} height="4" fill=${INK} />
        ${lbl(M.l + 30 + bar * kx + 6, PT + PH - 8, bar >= 1000 ? `${bar / 1000} km` : `${bar} m`, { size: 10, color: INK, weight: 600 })}
      </g>
      ${o.legend !== false && strata && html`<g>${(() => { let x = M.l; return strata.present.map((k) => {
        const name = units[k]?.name || '', w = 18 + name.length * 5.6 + 12, x0 = x; x += w;
        if (x > M.l + pw) return null;
        return html`<g><rect x=${x0} y=${M.t - 21} width="11" height="9" fill=${'#' + (viewer.C?.units?.[k] || viewer.geo.colors[k]).getHexString()} stroke="#7d776a" stroke-width="0.5" />
          ${lbl(x0 + 15, M.t - 13, name, { size: 9.5, color: INK2, weight: 500 })}</g>`; }); })()}</g>`}
    </svg>
    ${hover && !drag.current && (addMode && pick ? html`<div class="pf-tip" style=${`left:${Math.min(hover.x + 14, W - 270)}px;top:${Math.max(4, Math.min(hover.y + 12, H - 60))}px`}>
        <div>Click to label: <b>${pick.text}</b></div><div class="muted">${PICK_NAME[pick.kind] || pick.kind}${pick.d != null ? ` · ${Math.abs(pick.d).toFixed(0)} m ${pick.d >= 0 ? 'right' : 'left'} of the line` : ''}</div></div>`
      : hover.plan ? html`<div class="pf-tip" style=${`left:${Math.min(hover.x + 14, W - 270)}px;top:${Math.max(4, Math.min(hover.y + 12, H - 80))}px`}>
        <div><b>${Math.round(hover.s).toLocaleString()} m</b> along the line · ${Math.abs(hover.d).toFixed(0)} m ${hover.d >= 0 ? 'right' : 'left'} of it</div>
        ${hover.st && html`<div class="muted">Station ${hover.st.name} (click to open)</div>`}</div>`
      : html`<div class="pf-tip" style=${`left:${Math.min(hover.x + 14, W - 270)}px;top:${Math.max(4, Math.min(hover.y + 12, H - 120))}px`}>
      <div><b>${Math.round(hover.s).toLocaleString()} m</b> along the line · ground ${hover.gz != null ? `${hover.gz.toFixed(0)} m (${Math.round(hover.gz / FT).toLocaleString()} ft)` : '—'}</div>
      <div>Here: ${hover.z.toFixed(0)} m${hover.gz != null && (hover.z < hover.gz ? html` · <b>${(hover.gz - hover.z).toFixed(0)} m (${Math.round((hover.gz - hover.z) / FT)} ft)</b> below ground` : ` · ${(hover.z - hover.gz).toFixed(0)} m above ground`)}</div>
      ${hover.rock && html`<div><span class="sw" style=${'display:inline-block;width:9px;height:9px;border-radius:2px;margin-right:5px;background:' + hover.rock.unit.color}></span>${hover.rock.unit.name}${hover.rock.where ? `, ${hover.rock.where}` : ''}</div>`}
      ${hover.pq && hover.gz != null && html`<div>Passage ${hover.pq.dz < 0.5 ? 'here' : `${hover.pq.dz.toFixed(0)} m ${hover.z > hover.pq.top ? 'below' : 'above'}`}: roof ${hover.pq.top.toFixed(0)} m, floor ${hover.pq.bot.toFixed(0)} m ·
        <b>${(hover.gz - hover.pq.top).toFixed(0)} m (${Math.round((hover.gz - hover.pq.top) / FT)} ft) of cover</b>${hover.pq.d > 1 ? html` <span class="muted">· ${hover.pq.d.toFixed(0)} m off the line</span>` : ''}</div>`}
      ${hover.st && html`<div class="muted">Station ${hover.st.name}, ${hover.st.d.toFixed(0)} m off the line (click to open)</div>`}
    </div>`)}
    ${edl && html`<${LabelEditor} l=${edl} x=${X(edl.s)} y=${Y(edl.z)} W=${W} H=${H} planOn=${!!planMode}
      onChange=${(p) => onDoc({ labels: doc.labels.map(l => (l.id === edl.id ? { ...l, ...p } : l)) })}
      onDelete=${() => { onDoc({ labels: doc.labels.filter(l => l.id !== edl.id) }); setEdit(null); }} onClose=${() => setEdit(null)} />`}
    ${addMode && html`<div class="pf-hint">Click a feature (circled) on the profile${planMode ? ', the plan' : ''} or the 3D map to label it with its name, or anywhere for a blank label</div>`}
  </div>`;
}

function LabelEditor({ l, x, y, W, H, planOn, onChange, onDelete, onClose }) {
  const inp = useRef();
  useEffect(() => { inp.current?.focus(); inp.current?.select(); }, [l.id]);
  return html`<div class="pf-edit" style=${`left:${Math.max(4, Math.min(x + 8, W - 250))}px;top:${Math.max(4, Math.min(y + 10, H - 92))}px`}
      onPointerDown=${(e) => e.stopPropagation()}>
    <input ref=${inp} value=${l.text} maxlength="200" aria-label="Label text" onInput=${(e) => onChange({ text: e.target.value || ' ' })}
      onKeyDown=${(e) => { if (e.key === 'Enter' || e.key === 'Escape') onClose(); }} />
    <div class="row" style="gap:4px;margin-top:6px;justify-content:flex-start">
      ${LABEL_COLOURS.map(c => html`<button class=${'pf-sw' + ((l.color || INK) === c ? ' on' : '')} style=${'background:' + c} aria-label=${'Colour ' + c} onClick=${() => onChange({ color: c })}></button>`)}
      ${planOn && l.d != null && html`<label class="tiny chk" style="margin:0 0 0 4px" title="A line from where it is in the plan to the label"><input type="checkbox" checked=${l.tie !== false} onChange=${(e) => onChange({ tie: e.target.checked })} /> tie</label>`}
      <span style="flex:1"></span>
      <button class="btn sm danger" onClick=${onDelete}>Delete</button><button class="btn sm primary" onClick=${onClose}>Done</button></div>
  </div>`;
}
